import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppHeader } from "../Navigation/AppHeader";
import {
  COLLAPSED_KEY,
  Sidebar,
  readSidebarCollapsed,
} from "../Navigation/Sidebar";
import { Footer } from "../Footer/Footer";
import { AlertBanner, LayoutWrapper, MainContent } from "./Layout.styles";
import { useMyWorkspaces } from "../../hooks/queries/useWorkspaces";

/**
 * The application shell: fixed sidebar, sticky header, scrolling page.
 *
 * <p>The collapse state lives here rather than inside the sidebar because the page margin has to
 * track the sidebar width exactly; splitting them would let the two drift apart mid-animation.
 */
export const Layout = () => {
  const { t } = useTranslation(["billing", "common"]);
  const location = useLocation();
  const { data: myWorkspaces } = useMyWorkspaces();
  const activeWorkspace = myWorkspaces?.find((ws) => ws.active);

  const [collapsed, setCollapsed] = useState(readSidebarCollapsed);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSED_KEY, collapsed ? "1" : "0");
    } catch {
      // Preference simply will not persist; nothing else depends on it.
    }
  }, [collapsed]);

  // Navigating on a phone should leave the drawer behind, not stack pages under it.
  useEffect(() => setMobileOpen(false), [location.pathname]);

  const balance = activeWorkspace?.balanceUsd;
  const daysLeft = activeWorkspace?.estimatedDaysRemaining;

  return (
    <>
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((value) => !value)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <LayoutWrapper $collapsed={collapsed}>
        {balance !== undefined && balance <= 0 ? (
          <AlertBanner $tone="danger">
            {t("billing:suspended", "Account Suspended")}
            {" — "}
            {t(
              "billing:negative_warning",
              "Your balance is negative. Tracking links are suspended until you top up.",
            )}
          </AlertBanner>
        ) : daysLeft != null && daysLeft <= 3 ? (
          <AlertBanner $tone="warning">
            {t("billing:low_balance_warning", { days: daysLeft })}
          </AlertBanner>
        ) : null}

        <AppHeader onOpenMobileNav={() => setMobileOpen(true)} />

        <MainContent>
          <Outlet />
        </MainContent>

        <Footer />
      </LayoutWrapper>
    </>
  );
};
